/** @jsxImportSource react */

import { getVideoLdObject } from '@common/helpers/html_headers';
import { getImagePath } from '@common/helpers/images';
import { VideoProps } from '@react/shared/components/Video/Video.types';
import { Button, Heading } from '@react/system';
import useIntersectionObserver from '@react-hook/intersection-observer';
import {
  getVideoIframeUrl,
  isValidVimeoUrl,
  isValidYouTubeUrl,
} from '@sly/core/helpers/video';
import cx from 'classnames';
import dynamic from 'next/dynamic';
import Helmet from 'next/head';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const Modal = dynamic(
  () =>
    import(
      /* webpackChunkName: "chunkModal" */ '@react/shared/components/Modal'
    )
);

const ModalHeader = dynamic(
  () =>
    import(
      /* webpackChunkName: "chunkModalHeader" */ '@react/shared/components/Modal/header'
    )
);

export default function Video({
  title,
  url,
  iframe,
  path,
  isVideo,
  onFullscreen,
  onPause,
  className,
  alignCenter,
  onPlay,
  lazyLoad = true,
  transcript,
  info,
}: VideoProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const lockRef = useRef(false);
  const { isIntersecting } = useIntersectionObserver(containerRef);
  const [showTranscript, setShowTranscript] = useState(false);
  const toggleTranscript = useCallback(() => {
    setShowTranscript(!showTranscript);
  }, [showTranscript]);

  if (isIntersecting) {
    lockRef.current = true;
  }
  useEffect(() => {
    if (onFullscreen && containerRef.current) {
      // console.log('Added fullscreenchange event listener');
      const element = containerRef.current;
      element.addEventListener('webkitfullscreenchange', onFullscreen);
    }
  }, [isIntersecting, onFullscreen]);

  const transcriptText = useMemo(
    () => (transcript || '').split('\n').join('<br/>'),
    [transcript]
  );

  const showContent = lazyLoad ? !!isIntersecting : true;

  let videoSource = null;
  if (isVideo) {
    videoSource = path
      ? getImagePath(encodeURI(path?.replace(/\.jpe?g$/i, '.mp4')))
      : url;
  }

  const youtubeUrl = isValidYouTubeUrl(url) ? url : '';
  const vimeoUrl = isValidVimeoUrl(url) ? url : '';

  const iframeSchema =
    iframe && (youtubeUrl || vimeoUrl)
      ? getVideoLdObject({
          name: title,
          transcript: transcript || '',
          youtubeUrl: youtubeUrl,
          description: '',
          duration: info?.duration,
          uploadDate: info?.publishedAt,
          vimeoInfo: {
            vimeoUrl: vimeoUrl,
            vimeoThumbNail: null,
          },
        })
      : {};

  const iframeUrl = getVideoIframeUrl({ url, includePlayerParams: true });

  return (
    <div ref={containerRef}>
      <Helmet>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'VideoObject',
              name: title,
              description: title,
              transcript: transcript,
              ...iframeSchema,
            }),
          }}
        />
      </Helmet>
      {iframe && showContent && (
        <div className={alignCenter ? 'self-center' : ''}>
          <iframe
            title={title}
            src={iframeUrl}
            frameBorder='0'
            allow='accelerometer; encrypted-media; gyroscope; picture-in-picture'
            allowFullScreen
            className={cx(
              'mx-auto mb-l h-[315px] w-[calc(100%-2rem)]',
              'md:mb-xl md:w-[var(--col6)] lg:w-[var(--col8)]',
              className
            )}
          />
        </div>
      )}
      {isVideo && showContent && (
        <div className={alignCenter ? 'self-center' : ''}>
          <video
            title={title}
            className={cx(
              'mx-auto mb-l h-[315px] w-[calc(100%-2rem)]',
              'md:mb-xl md:w-[var(--col6)] lg:w-[var(--col8)]',
              className && className
            )}
            controls
            controlsList='nodownload'
            src={`${videoSource}#t=0.001`}
            onPause={onPause}
            onPlay={onPlay}
          ></video>
        </div>
      )}
      {transcript && (
        <>
          <Button
            className={cx('mb-l')}
            variant='outlined'
            palette='slate'
            onClick={toggleTranscript}
          >
            Show transcript
          </Button>
          <Modal
            toggleModal={toggleTranscript}
            isOpen={showTranscript}
            panelClassName='max-w-[680px] p-l'
          >
            <ModalHeader toggleModal={toggleTranscript}></ModalHeader>
            <Heading as='h3' font='font-t-m' className='mb-l'>
              {title}
            </Heading>
            <div dangerouslySetInnerHTML={{ __html: transcriptText }} />
          </Modal>
        </>
      )}
    </div>
  );
}
